/**
 * Demo colours, zod-free so the player can import it (`@mado/schema/color`)
 * without pulling zod into its budget.
 *
 * The defaults are mado's own look (DESIGN-SYSTEM.md §1, the Sept 2026
 * overhaul): a new demo gets rust as its accent and ink as its background. A
 * document stores its theme once parsed, so changing these defaults never
 * repaints a draft or a published version that already has colours.
 */

/** Brand rust — the accent a new demo starts with. */
export const DEFAULT_ACCENT = '#A8561C';
/** Warm charcoal — the letterbox/background (and step-tip surface) a new demo
 *  starts with. Was brand ink #3A2114 until 2026-09-28 (no browns on dark,
 *  DESIGN-SYSTEM §1); demos that stored ink keep it. */
export const DEFAULT_BACKGROUND = '#22201C';

/** Text drawn on an accent-filled control: deep ink or cream, whichever reads
 *  better. Deep ink (not #3A2114) so demos published with the old amber
 *  accent keep their CTA text above 4.5:1 (5.42:1; it was 5.15:1 on graphite). */
export const ON_ACCENT_DARK = '#1F120B';
export const ON_ACCENT_LIGHT = '#FFF8EE';

function channel(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#rrggbb`, or null if malformed. */
export function luminance(hex: string): number | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return (
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  );
}

/** WCAG contrast ratio between two hex colours, or null if either is malformed. */
export function contrastRatio(a: string, b: string): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The text colour for a button or pill filled with `accent`: ink on a light
 * accent, cream on a dark one — whichever contrasts more. The player, the
 * editor's contrast warning and the MP4 export all call this, so they agree.
 */
export function onAccent(accent: string): string {
  const dark = contrastRatio(accent, ON_ACCENT_DARK) ?? 0;
  const light = contrastRatio(accent, ON_ACCENT_LIGHT) ?? 0;
  return light > dark ? ON_ACCENT_LIGHT : ON_ACCENT_DARK;
}

/**
 * The popup surface (tooltip, end card, intro/chapter card) derived from the
 * demo's own `theme.background` — never brand rust/ink, so a customer's demo
 * never wears mado's colours where it isn't the badge. `surface` is white on
 * a light background, a raised near-black neutral on a dark one; `fg` is the
 * same ink/cream pairing `onAccent` would choose for that background, read as
 * body text rather than button text. `fgDim` and `border` are fixed low-
 * contrast companions for secondary text and hairlines. The player, the MP4
 * export (services/pipeline/src/export/frame.ts) and the editor's settings
 * preview all call this, so a tooltip and the export's drawn one agree.
 */
export const SURFACE_DARK = '#1B1C1E';
export const SURFACE_LIGHT = '#FFFFFF';
const SURFACE_FG_DIM_DARK = '#A9A4A0';
const SURFACE_FG_DIM_LIGHT = '#6E6A65';
const SURFACE_BORDER_DARK = 'rgba(255,255,255,0.10)';
const SURFACE_BORDER_LIGHT = 'rgba(20,18,16,0.10)';

export interface Surface {
  /** Card/tooltip fill. */
  surface: string;
  /** Title/body text on `surface`. */
  fg: string;
  /** Secondary text (eyebrow, caption, muted line) on `surface`. */
  fgDim: string;
  /** A hairline border/divider that reads on `surface`. */
  border: string;
}

export function surfaceFor(background: string): Surface {
  const dark = onAccent(background) === ON_ACCENT_LIGHT;
  return dark
    ? {
        surface: SURFACE_DARK,
        fg: ON_ACCENT_LIGHT,
        fgDim: SURFACE_FG_DIM_DARK,
        border: SURFACE_BORDER_DARK,
      }
    : {
        surface: SURFACE_LIGHT,
        fg: ON_ACCENT_DARK,
        fgDim: SURFACE_FG_DIM_LIGHT,
        border: SURFACE_BORDER_LIGHT,
      };
}

/**
 * The colour most of a row of pixels has (`data` as RGB or RGBA, `stride` 3 or 4): pixels are
 * grouped by colour (32 levels a channel) and the biggest group's average is returned. Fills
 * round a whole screen use it, not the row's average: an edge of cream page crossing dark
 * thumbnails averaged to grey (2026-10-21).
 */
export function commonColor(data: ArrayLike<number>, stride: number): [number, number, number] {
  const groups = new Map<number, [number, number, number, number]>();
  let best: [number, number, number, number] = [0, 0, 0, 0];
  for (let i = 0; i + 2 < data.length; i += stride) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    let s = groups.get(key);
    if (!s) groups.set(key, (s = [0, 0, 0, 0]));
    s[0] += r;
    s[1] += g;
    s[2] += b;
    s[3] += 1;
    if (s[3] > best[3]) best = s;
  }
  const n = Math.max(1, best[3]);
  return [Math.round(best[0] / n), Math.round(best[1] / n), Math.round(best[2] / n)];
}
